<template>
  <div
    class="min-h-screen w-full flex flex-col bg-gradient-to-br from-spicy-mustard-300 to-mocha-mousse-300"
  >
    <!-- Header Bar -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <!-- Main Content -->
    <div class="flex-1 px-6 py-10">
      <div class="max-w-7xl mx-auto w-full">
        <!-- Header -->
        <div class="mb-10 text-center">
          <h1 class="text-4xl font-bold text-mocha-mousse-900">
            System Logs
          </h1>
          <p class="mt-3 text-mocha-mousse-700 text-lg">
            View and filter system activity logs
          </p>
        </div>

        <!-- Filters -->
        <div class="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-8 mb-8">
          <h2 class="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Icon icon="mdi:filter" class="text-mocha-mousse-600" />
            Filters
          </h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Source Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Source</label>
              <select
                v-model="filters.source"
                @change="fetchLogs"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              >
                <option value="">All Sources</option>
                <option value="backend">Backend</option>
                <option value="frontend">Frontend</option>
              </select>
            </div>

            <!-- Level Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Level</label>
              <select
                v-model="filters.level"
                @change="fetchLogs"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              >
                <option value="">All Levels</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <!-- Error Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Error Status</label>
              <select
                v-model="filters.is_error"
                @change="fetchLogs"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              >
                <option :value="null">All Logs</option>
                <option :value="true">Errors Only</option>
                <option :value="false">No Errors</option>
              </select>
            </div>

            <!-- User Filter -->
            <UserSearchComponent @user-selected="onUserSelected" />

            <!-- Search -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                v-model="filters.search"
                @keyup.enter="fetchLogs"
                type="text"
                placeholder="Search logs..."
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              />
            </div>

            <!-- Date Range -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                v-model="filters.start_date"
                @change="fetchLogs"
                type="datetime-local"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                v-model="filters.end_date"
                @change="fetchLogs"
                type="datetime-local"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500 transition-shadow"
              />
            </div>

            <!-- Action Buttons -->
            <div class="flex items-end gap-2 lg:col-span-2">
              <button
                @click="clearFilters"
                class="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mocha-mousse-500 transition-colors"
              >
                <Icon icon="mdi:filter-remove-outline" class="mr-2 h-4 w-4" />
                Clear Filters
              </button>
              <button
                @click="fetchLogs"
                :disabled="loading"
                class="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-mocha-mousse-600 hover:bg-mocha-mousse-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mocha-mousse-500 disabled:opacity-50 transition-colors"
              >
                <Icon
                  icon="mdi:refresh"
                  class="mr-2 h-4 w-4"
                  :class="{ 'animate-spin': loading }"
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <!-- Results Summary with Pagination Top -->
        <div class="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm p-4 mb-6">
          <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
            <!-- Per Page Selector -->
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600">Show:</label>
              <select
                v-model="perPage"
                @change="onPerPageChange"
                class="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 focus:border-mocha-mousse-500"
              >
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="200">200</option>
              </select>
              <span class="text-sm text-gray-600">per page</span>
            </div>

            <!-- Pagination Top -->
            <Pagination
              v-if="totalPages > 1"
              :current-page="currentPage"
              :total-pages="totalPages"
              :total-items="totalLogs"
              :items-per-page="perPage"
              :show-summary="false"
              @first="goToFirstPage"
              @previous="prevPage"
              @page="goToPage"
              @next="nextPage"
              @last="goToLastPage"
            />
          </div>
        </div>

        <!-- Logs Table -->
        <div class="bg-white/95 backdrop-blur-sm rounded-xl shadow-sm overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200/50">
              <thead class="bg-mocha-mousse-50/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Source
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Level
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Event
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Details
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    User Info
                  </th>
                  <th class="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white/40 divide-y divide-gray-200/50">
                <tr v-for="log in logs" :key="log.id" class="hover:bg-mocha-mousse-50/30 transition-colors">
                  <!-- Timestamp -->
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDateTime(log.timestamp) }}
                  </td>

                  <!-- Source -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full"
                      :class="getSourceClass(log.source)"
                    >
                      {{ log.source }}
                    </span>
                  </td>

                  <!-- Level -->
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full"
                      :class="getLevelClass(log.level)"
                    >
                      {{ log.level }}
                    </span>
                  </td>

                  <!-- Event -->
                  <td class="px-6 py-4 text-sm text-gray-900">
                    <div class="font-medium">{{ log.event }}</div>
                    <div v-if="log.category" class="text-gray-500 text-xs">
                      {{ log.category }}
                    </div>
                  </td>

                  <!-- Details -->
                  <td class="px-6 py-4 text-sm text-gray-700">
                    <div v-if="log.endpoint" class="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {{ log.http_method }} {{ log.endpoint }}
                    </div>
                    <div v-if="log.status_code" class="text-xs mt-1">
                      Status: <span class="font-medium">{{ log.status_code }}</span>
                    </div>
                    <div v-if="log.duration_ms" class="text-xs">
                      {{ log.duration_ms }}ms
                    </div>
                    <div v-if="log.page" class="text-xs text-gray-600">
                      Page: {{ log.page }}
                    </div>
                    <div v-if="log.meta?.operation" class="text-xs text-gray-600">
                      Op: <span class="font-mono">{{ log.meta.operation }}</span>
                    </div>
                    <div v-if="log.meta?.is_api !== undefined" class="text-xs">
                      API: <span class="font-medium">{{ log.meta.is_api ? 'Yes' : 'No' }}</span>
                    </div>
                    <div v-if="log.meta?.is_public !== undefined" class="text-xs">
                      Public: <span class="font-medium">{{ log.meta.is_public ? 'Yes' : 'No' }}</span>
                    </div>
                    <div
                      v-if="log.error_message"
                      class="text-xs text-red-600 truncate max-w-xs mt-1"
                    >
                      {{ log.error_message }}
                    </div>
                  </td>

                  <!-- User Info -->
                  <td class="px-6 py-4 text-sm text-gray-600">
                    <div v-if="log.vuo_id" class="font-medium text-gray-900">
                      ID: {{ log.vuo_id }}
                    </div>
                    <div
                      v-if="log.session_id"
                      class="text-xs text-gray-400 truncate max-w-xs"
                    >
                      {{ log.session_id }}
                    </div>
                    <div v-if="log.ip_address" class="text-xs">
                      {{ log.ip_address }}
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      @click="openLogModal(log)"
                      class="inline-flex items-center justify-center w-8 h-8 rounded-full text-mocha-mousse-600 hover:bg-mocha-mousse-100 hover:text-mocha-mousse-900 focus:outline-none focus:ring-2 focus:ring-mocha-mousse-500 transition-colors"
                      title="View details"
                    >
                      <Icon icon="mdi:eye" class="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && logs.length === 0" class="text-center py-16">
            <Icon
              icon="mdi:file-document-outline"
              class="mx-auto h-16 w-16 text-gray-400"
            />
            <h3 class="mt-4 text-lg font-medium text-gray-900">No logs found</h3>
            <p class="mt-2 text-sm text-gray-500">Try adjusting your filters</p>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-16">
            <Icon
              icon="mdi:loading"
              class="mx-auto h-16 w-16 text-mocha-mousse-500 animate-spin"
            />
            <p class="mt-4 text-sm text-gray-500">Loading logs...</p>
          </div>
        </div>

        <!-- Pagination Bottom -->
        <div v-if="totalPages > 1" class="mt-6">
          <Pagination
            :current-page="currentPage"
            :total-pages="totalPages"
            :total-items="totalLogs"
            :items-per-page="perPage"
            @first="goToFirstPage"
            @previous="prevPage"
            @page="goToPage"
            @next="nextPage"
            @last="goToLastPage"
          />
        </div>
      </div>
    </div>

    <!-- Log Detail Modal -->
    <LogDetailModal
      :is-open="isModalOpen"
      :log="selectedLog"
      @close="closeModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Icon } from "@iconify/vue";
import api from "@/services/api";
import UserSearchComponent from "@/components/UserSearchComponent.vue";
import LogDetailModal from "@/components/LogDetailModal.vue";
import Pagination from "@/components/Pagination.vue";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";

const logs = ref([]);
const totalLogs = ref(0);
const currentPage = ref(1);
const perPage = ref(50);
const loading = ref(false);
const logo = ref("");

const filters = ref({
  source: "",
  level: "",
  is_error: null,
  search: "",
  start_date: "",
  end_date: "",
  user_filter: null,
});

const selectedLog = ref(null);
const isModalOpen = ref(false);

const totalPages = computed(() => Math.ceil(totalLogs.value / perPage.value));

const goToFirstPage = () => {
  currentPage.value = 1;
  fetchLogs();
};

const goToLastPage = () => {
  currentPage.value = totalPages.value;
  fetchLogs();
};

const getSourceClass = (source) => {
  switch (source) {
    case "backend":
      return "bg-blue-100 text-blue-800";
    case "frontend":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getLevelClass = (level) => {
  switch (level) {
    case "debug":
      return "bg-gray-100 text-gray-700";
    case "info":
      return "bg-blue-100 text-blue-800";
    case "warning":
      return "bg-yellow-100 text-yellow-800";
    case "error":
      return "bg-red-100 text-red-800";
    case "critical":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const fetchLogs = async () => {
  try {
    loading.value = true;

    const params = {
      page: currentPage.value,
      per_page: perPage.value,
      ...filters.value,
    };

    // Remove null/undefined values
    Object.keys(params).forEach((key) => {
      if (
        params[key] === null ||
        params[key] === undefined ||
        params[key] === ""
      ) {
        delete params[key];
      }
    });

    const response = await api.getAdminLogs(params);

    const data = response.data.message;
    logs.value = data.logs || [];
    totalLogs.value = data.total || 0;
  } catch (error) {
    console.error("Failed to fetch logs:", error);
    logs.value = [];
    totalLogs.value = 0;
  } finally {
    loading.value = false;
  }
};

const clearFilters = () => {
  filters.value = {
    source: "",
    level: "",
    is_error: null,
    search: "",
    start_date: "",
    end_date: "",
    user_filter: null,
  };
  currentPage.value = 1;
  fetchLogs();
};

const onUserSelected = (userId) => {
  filters.value.user_filter = userId;
  fetchLogs();
};

const openLogModal = (log) => {
  selectedLog.value = log;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedLog.value = null;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchLogs();
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchLogs();
  }
};

const goToPage = (page) => {
  currentPage.value = page;
  fetchLogs();
};

const onPerPageChange = () => {
  // Reset to first page when changing items per page
  currentPage.value = 1;
  fetchLogs();
};

onMounted(() => {
  fetchLogs();
  logo.value = `${process.env.BASE_URL}images/enodo-logo.png`;
});
</script>
